import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isEqual, debounce } from 'lodash';
import { validate, mergeStyles, GlobalContext } from 'wix-rich-content-common';
import pluginFileUploadSchema from 'wix-rich-content-common/dist/statics/schemas/plugin-file-upload.schema.json';
import { f as fileStyles } from './file-upload-viewer-6b6742c3.js';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import classnames from 'classnames';
import { FILE_UPLOAD_TYPE } from './types.js';
import { PDF_STATUS, PDF_OPTIONS } from './pdfViewer/pdfConsts.js';
import ErrorIcon from './icons/errorIcon.js';
import LoaderIcon from './icons/LoaderIcon.js';
import ReadyIcon from './icons/readyIcon.js';
import DownloadIcon from './icons/downloadIcon.js';
import { getIcon } from './icons/getIcon.js';
import { PDFViewer } from './pdfViewer/pdfViewer.js';
import './style-inject.es-746bb8ed.js';
import './icons/xlsIcon.js';
import './icons/vid-aud-icon.js';
import './icons/pptIcon.js';
import './icons/pdfIcon.js';
import './icons/otherIcon.js';
import './icons/imageIcon.js';
import './icons/folderIcon.js';
import './icons/docIcon.js';
import './pdfViewer/pdfViewSDKClient.js';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const getNameWithoutType = (fileName) => {
  if (!fileName || !fileName.includes(".")) {
    return fileName;
  }
  const s = fileName.split(".");
  return s.slice(0, s.length - 1).join(".");
};
const resizeWidths = { first: 320, second: 140, third: 100 };
class FileUploadViewer extends PureComponent {
  constructor(props) {
    super(props);
    __publicField(this, "isInResizeRange", (resizeWidth) => {
      var _a, _b;
      return ((_b = (_a = this.fileUploadViewerRef) == null ? void 0 : _a.current) == null ? void 0 : _b.clientWidth) < resizeWidth;
    });
    __publicField(this, "switchReadyIcon", () => {
      return this.setState(
        { showReadyIcon: true },
        () => setTimeout(() => this.setState({ showReadyIcon: false }), 2e3)
      );
    });
    __publicField(this, "renderContainerWithoutLink", () => {
      const { tempDataPlaceHolder, componentData } = this.props;
      const { name, type } = tempDataPlaceHolder ? tempDataPlaceHolder : componentData;
      return /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classnames(this.styles.file_upload_link, {
            [this.styles.width_three]: this.isInResizeRange(resizeWidths.third)
          })
        },
        this.renderViewerBody({ name, type })
      );
    });
    __publicField(this, "renderIcon", (Icon) => {
      const {
        isLoading,
        isMobile,
        componentData: { error }
      } = this.props;
      const { showReadyIcon, resolvingUrl, pdfStatus } = this.state;
      const showLoader = isLoading || resolvingUrl || pdfStatus === PDF_STATUS.LOADING_FILE;
      const showFileIcon = !showLoader && !showReadyIcon && isMobile || !isMobile && Icon;
      if (showFileIcon) {
        return /* @__PURE__ */ React.createElement(
          Icon,
          {
            styles: this.styles,
            className: classnames(this.styles.file_upload_type_icon, {
              [this.styles.width_three]: this.isInResizeRange(resizeWidths.third)
            })
          }
        );
      } else {
        return /* @__PURE__ */ React.createElement("div", { className: isMobile ? this.styles.mobile_status_icon : this.styles.file_upload_state }, error ? /* @__PURE__ */ React.createElement(ErrorIcon, null) : showLoader ? /* @__PURE__ */ React.createElement(LoaderIcon, { className: this.styles.file_loader_icon }) : showReadyIcon ? /* @__PURE__ */ React.createElement(ReadyIcon, null) : !this.isInResizeRange(resizeWidths.first) && /* @__PURE__ */ React.createElement(DownloadIcon, null));
      }
    });
    __publicField(this, "sizeToString", (size) => {
      return size < 1e3 ? size + "B" : size < 1e6 ? Math.round(size / 1e3) + "KB" : (size / 1e6).toFixed(2) + "MB";
    });
    __publicField(this, "getExperiments", () => this.props.experiments || this.context.experiments);
    __publicField(this, "onFileClick", () => {
      var _a, _b;
      return (_b = (_a = this.props.helpers).onViewerAction) == null ? void 0 : _b.call(_a, FILE_UPLOAD_TYPE, "Click");
    });
    __publicField(this, "onPDFStatusChange", (status) => this.setState({ pdfStatus: status }));
    const {
      componentData,
      componentData: { type, pdfSettings },
      settings: { adobeAPIKey }
    } = props;
    const viewMode = (pdfSettings == null ? void 0 : pdfSettings.viewMode) || PDF_OPTIONS.NONE;
    validate(componentData, pluginFileUploadSchema);
    this.downloaderRef = React.createRef();
    this.fileUploadViewerRef = React.createRef();
    const pdfStatus = type === "pdf" && viewMode !== PDF_OPTIONS.NONE && adobeAPIKey ? PDF_STATUS.PENDING : PDF_STATUS.NONE;
    this.state = {
      resolvedFileUrl: null,
      resolvingUrl: false,
      currentWidth: 0,
      pdfStatus
    };
  }
  componentWillReceiveProps(nextProps) {
    const {
      componentData: nextComponentData,
      settings: { adobeAPIKey }
    } = nextProps;
    const { componentData } = this.props;
    if (!isEqual(nextComponentData, componentData)) {
      validate(nextComponentData, pluginFileUploadSchema);
    }
    if (!nextProps.isLoading && this.props.isLoading) {
      this.switchReadyIcon();
    }
  }
  componentDidMount() {
    if (window == null ? void 0 : window.ResizeObserver) {
      this.resizer = new ResizeObserver(
        debounce((entries) => {
          this.setState({ currentWidth: Math.round(entries[0].contentRect.width) });
        }, 60)
      );
      this.resizer.observe(this.fileUploadViewerRef.current);
    }
  }
  componentWillUnmount() {
    var _a;
    (_a = this.resizeObserver) == null ? void 0 : _a.unobserve(this.fileUploadViewerRef.current);
  }
  getFileInfoString(type) {
    const { componentData, t, isLoading, tempDataPlaceHolder } = this.props;
    const { resolvingUrl } = this.state;
    if (componentData.error) {
      return {
        infoString: t("UploadFile_Error_Generic_Item"),
        infoStyle: this.styles.file_upload_text_error
      };
    }
    const translationKey = isLoading || resolvingUrl ? "UploadFile_Viewer_Loader" : "UploadFile_Viewer_Download";
    let infoString = t(translationKey, {
      fileType: type == null ? void 0 : type.toUpperCase()
    });
    const size = componentData.size || (tempDataPlaceHolder == null ? void 0 : tempDataPlaceHolder.size);
    if (size) {
      infoString = infoString + " \u2022 " + this.sizeToString(size);
    }
    return { infoString, infoStyle: this.styles.file_upload_type };
  }
  renderViewerBody({ name, type }) {
    const { isMobile } = this.props;
    const nameWithoutType = getNameWithoutType(name);
    const Icon = getIcon(type);
    const { infoString, infoStyle } = this.getFileInfoString(type);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, this.renderIcon(Icon), !isMobile && this.renderIcon(), !this.isInResizeRange(resizeWidths.third) && /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames(this.styles.file_upload_text_container, {
          [this.styles.width_two]: this.isInResizeRange(resizeWidths.second)
        })
      },
      /* @__PURE__ */ React.createElement("div", { className: this.styles.file_upload_name_container }, !this.isInResizeRange(resizeWidths.second) && /* @__PURE__ */ React.createElement("div", { className: this.styles.file_upload_name }, nameWithoutType), type && /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classnames(this.styles.file_upload_extension, {
            [this.styles.width_one]: this.isInResizeRange(resizeWidths.first)
          })
        },
        "." + type
      )),
      /* @__PURE__ */ React.createElement("div", { className: infoStyle }, infoString)
    ));
  }
  renderViewer(fileUrl) {
    const {
      componentData: { name, type, error },
      tempDataPlaceHolder
    } = this.props;
    const { downloadTarget } = this.props.settings;
    if (error || tempDataPlaceHolder) {
      return this.renderContainerWithoutLink();
    }
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        href: fileUrl,
        target: downloadTarget,
        className: classnames(this.styles.file_upload_link, {
          [this.styles.width_three]: this.isInResizeRange(resizeWidths.third)
        }),
        onClick: this.onFileClick
      },
      this.renderViewerBody({ name, type })
    );
  }
  renderFileUrlResolver() {
    const {
      componentData,
      settings: { resolveFileUrl }
    } = this.props;
    const { name, type, error } = componentData;
    if (error) {
      return this.renderContainerWithoutLink();
    }
    const fileUrlResolver = () => {
      const { resolvingUrl } = this.state;
      if (!resolvingUrl) {
        this.setState({ resolvingUrl: true });
        resolveFileUrl(componentData).then((resolvedFileUrl) => {
          this.setState({ resolvedFileUrl, resolvingUrl: false }, this.switchReadyIcon);
          if (this.downloaderRef.current) {
            this.downloaderRef.current.href = resolvedFileUrl;
            this.downloaderRef.current.download = name;
            this.downloaderRef.current.click();
          }
        });
      }
    };
    const resolveIfEnter = (ev) => {
      const enterEvent = 13;
      if (ev.which === enterEvent) {
        fileUrlResolver();
      }
    };
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        onClick: fileUrlResolver,
        onKeyDown: resolveIfEnter,
        role: "button",
        tabIndex: 0,
        className: classnames(this.styles.file_upload_link, {
          [this.styles.width_three]: this.isInResizeRange(resizeWidths.third)
        })
      },
      this.renderViewerBody({ name, type })
    );
  }
  renderAutoDownloadLinkRef() {
    const {
      settings: { resolveFileUrl: withFileUrlResolver, downloadTarget }
    } = this.props;
    if (!withFileUrlResolver) {
      return null;
    }
    const target = downloadTarget || "_blank";
    this.getExperiments();
    return /* @__PURE__ */ React.createElement("a", { href: "#0", ref: this.downloaderRef, style: { display: "none" }, target }, /* @__PURE__ */ React.createElement(React.Fragment, null));
  }
  render() {
    var _a;
    const {
      componentData,
      componentData: { pdfSettings = {} },
      theme,
      setComponentUrl,
      tempDataPlaceHolder,
      settings: { adobeAPIKey },
      locale,
      iframeSandboxDomain
    } = this.props;
    const { pdfStatus } = this.state;
    this.styles = this.styles || mergeStyles({ styles: fileStyles, theme });
    const fileUrl = componentData.url || this.state.resolvedFileUrl;
    setComponentUrl == null ? void 0 : setComponentUrl(fileUrl);
    const viewer = fileUrl || tempDataPlaceHolder ? this.renderViewer(fileUrl) : this.renderFileUrlResolver();
    const dataHook = "fileUploadViewer";
    const shouldRenderPDFViewer = ((_a = this.getExperiments().enableFilePluginPDFViewer) == null ? void 0 : _a.enabled) && typeof window !== "undefined" && fileUrl && ![PDF_STATUS.ERROR].includes(pdfStatus);
    const style = classnames(
      this.styles.file_upload_container,
      componentData.error && this.styles.file_upload_error_container
    );
    return /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        content: this.isInResizeRange(resizeWidths.first) && componentData.name,
        tooltipOffset: { y: 25 }
      },
      shouldRenderPDFViewer ? /* @__PURE__ */ React.createElement("div", { "data-hook": dataHook, ref: this.fileUploadViewerRef }, /* @__PURE__ */ React.createElement(
        PDFViewer,
        {
          status: pdfStatus,
          fileData: { url: fileUrl, fileName: componentData.name },
          setStatus: this.onPDFStatusChange,
          pdfSettings,
          config: {
            clientId: adobeAPIKey,
            locale
          }
        }
      ), ![PDF_STATUS.READY].includes(pdfStatus) && /* @__PURE__ */ React.createElement("div", { className: style }, viewer)) : /* @__PURE__ */ React.createElement("div", { "data-hook": dataHook, ref: this.fileUploadViewerRef, className: style }, viewer, this.renderAutoDownloadLinkRef())
    );
  }
}
__publicField(FileUploadViewer, "contextType", GlobalContext);
FileUploadViewer.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  componentData: PropTypes.object.isRequired,
  tempDataPlaceHolder: PropTypes.object,
  settings: PropTypes.object,
  theme: PropTypes.object.isRequired,
  setComponentUrl: PropTypes.func,
  t: PropTypes.func,
  isMobile: PropTypes.bool,
  helpers: PropTypes.object,
  locale: PropTypes.string,
  iframeSandboxDomain: PropTypes.string,
  experiments: PropTypes.object
};
FileUploadViewer.defaultProps = {
  isLoading: false,
  settings: {}
};

export { FileUploadViewer as default };
//# sourceMappingURL=file-upload-viewer.js.map
